import { Bullseye, Button, Grid, GridItem, ToggleGroup, ToggleGroupItem } from "@patternfly/react-core";
import { Modal, ModalVariant } from "@patternfly/react-core/deprecated";
import { FormContainer } from "@rhc-shared-components/form";
import { FormRadio } from "@rhc-shared-components/form-radio-select";
import { FormTextInput } from "@rhc-shared-components/form-text-input";
import { Formik, type FormikProps } from "formik";
import { type FC, useEffect, useRef, useState } from "react";
import { AttachExistingComponents } from "./AttachExistingComponents";
import { initialModalValue, useCreateModalStore } from "./CreateModalStore";
import {
	ComponentCreateValidator,
	ComponentTypes,
	CreateFormKeys,
	OpenStackInfraPlatformComponentCreateTypes,
	OpenStackVersions,
	OpenstackCreateModalInitialValues,
	OpenstackCreateModalValidator,
	ProjectCreateModalKeys,
	ProjectNonContKeys,
	useCreateAndAttachComponent,
	useCreateAndAttachNonContainerComponent,
} from "./createConstants";
import { OpenStackInfraCreateForm } from "./Forms/OpenStackInfraCreateForm";

interface OpenStackInfraComponentModalProps {
	handleCreateModal: (showModal: boolean) => void;
}

export const OpenStackInfraComponentModal: FC<OpenStackInfraComponentModalProps> = (
	props: OpenStackInfraComponentModalProps,
) => {
	const step1Ref = useRef<FormikProps<Record<string, string>>>(null);
	const step2Ref = useRef<FormikProps<Record<string, string>>>(null);
	const attachRef = useRef<FormikProps<Record<string, string>>>(null);
	const [modalType, setModalType] = useState<"new" | "existing">("new");
	const { createNewNonContainerComponent, isCreateNonContainerComponentAPILoading, isProductDetailsLoading } =
		useCreateAndAttachNonContainerComponent(props.handleCreateModal, "new");
	const { isAttachComponentAPILoading, isCreateComponentAPILoading, checkAttachmentError } =
		useCreateAndAttachComponent(props.handleCreateModal, "new");
	const { setModalData, modalData, resetModalData } = useCreateModalStore();
	const modalButtonStatus =
		isProductDetailsLoading ||
		isAttachComponentAPILoading ||
		isCreateNonContainerComponentAPILoading ||
		isCreateComponentAPILoading;
	const handleModalTypeChange = (_selected: any, event: any) => {
		const newStatus = event.currentTarget.id;
		setModalType(newStatus);
	};

	useEffect(() => {
		document.body.classList.add("stop-scrolling");
		return () => {
			resetModalData();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resetModalData]);

	return (
		<>
			<Modal
				title="Add Component"
				variant={ModalVariant.medium}
				id="add-component-modal"
				className="add-component-modal"
				appendTo={document.getElementById("modal-div-container") ?? document.body}
				isOpen
				onClose={() => {
					props.handleCreateModal(false);
					document.body.classList.remove("stop-scrolling");
				}}
				style={modalData.pageChange ? { display: "none" } : { display: "block" }}
				actions={[
					<Button
						key="create"
						variant="primary"
						isLoading={
							isProductDetailsLoading || isCreateNonContainerComponentAPILoading || isAttachComponentAPILoading
						}
						isDisabled={
							isProductDetailsLoading || isCreateNonContainerComponentAPILoading || isAttachComponentAPILoading
						}
						{...(modalType === "new"
							? modalData.componentType !== ComponentTypes.openStackVnf
								? {}
								: {
										"data-analytics-category": "Product Info|Add Component",
									}
							: { "data-analytics-category": "Product Info|Attach Existing Component" })}
						onClick={() => {
							modalType === "new" ? step1Ref?.current?.handleSubmit() : attachRef?.current?.handleSubmit();
						}}
					>
						{`${modalType === "new" ? (modalData.componentType !== ComponentTypes.openStackVnf ? "Next" : "Create new component") : "Attach existing component"}`}
					</Button>,
					<Button
						key="cancel"
						isDisabled={
							isProductDetailsLoading || isCreateNonContainerComponentAPILoading || isAttachComponentAPILoading
						}
						variant="link"
						onClick={() => {
							props.handleCreateModal(false);
							document.body.classList.remove("stop-scrolling");
						}}
					>
						Cancel
					</Button>,
				]}
			>
				<Grid hasGutter>
					<GridItem>
						<Bullseye>
							<ToggleGroup>
								<ToggleGroupItem
									text="New Component"
									buttonId="new"
									isSelected={modalType === "new"}
									isDisabled={
										isProductDetailsLoading || isCreateNonContainerComponentAPILoading || isAttachComponentAPILoading
									}
									onChange={(event, selected: any) => handleModalTypeChange(selected, event)}
								/>
								<ToggleGroupItem
									text="Existing Component"
									buttonId="existing"
									isSelected={modalType === "existing"}
									isDisabled={
										isProductDetailsLoading || isCreateNonContainerComponentAPILoading || isAttachComponentAPILoading
									}
									onChange={(event, selected: any) => handleModalTypeChange(selected, event)}
								/>
							</ToggleGroup>
						</Bullseye>
					</GridItem>
					<GridItem>
						<div style={modalType === "existing" ? { display: "none" } : { display: "block" }}>
							<Formik
								innerRef={step1Ref}
								initialValues={OpenstackCreateModalInitialValues}
								validationSchema={() => {
									return ComponentCreateValidator.concat(OpenstackCreateModalValidator);
								}}
								onSubmit={async (values) => {
									if (values[CreateFormKeys.componentType] === ComponentTypes.openStackVnf) {
										const requestData = {
											[ProjectCreateModalKeys.name]: values[CreateFormKeys.componentName],
											[ProjectNonContKeys.projectStatus]: { code: "active" },
											[ProjectNonContKeys.rhProductName]: "OpenStack",
											[ProjectNonContKeys.rhProductVersion]: values[CreateFormKeys.productVersion],
											[ProjectNonContKeys.type]: { code: ComponentTypes.openStackVnf },
										};
										if (checkAttachmentError([requestData])) {
											return false;
										}
										await createNewNonContainerComponent.mutateAsync(requestData);
									} else {
										setModalData({
											pageChange: true,
											componentType: values[CreateFormKeys.componentType],
											componentName: values[CreateFormKeys.componentName],
											productVersion: values[CreateFormKeys.productVersion],
										});
									}
								}}
							>
								<FormContainer gridSpan={12}>
									<FormTextInput
										name={CreateFormKeys.componentName}
										label={"Component Name"}
										isRequired
										helperText={"This is for internal use only and will not be visible to customers"}
									/>
									<FormRadio
										name={CreateFormKeys.componentType}
										label={"What kind of OpenStack component are you creating?"}
										isRequired={true}
										options={OpenStackInfraPlatformComponentCreateTypes}
										onChange={(selectedValue: string) => {
											setModalData({
												componentType: selectedValue,
											});
										}}
										span={12}
									/>
									<FormRadio
										name={CreateFormKeys.productVersion}
										label={`Red Hat OpenStack version`}
										isRequired={true}
										options={OpenStackVersions}
										subLabel={
											<div style={{ marginBottom: "0.5rem" }}>
												What major version of OpenStack will you be certifying your image for?{" "}
											</div>
										}
										span={12}
									/>
								</FormContainer>
							</Formik>
						</div>
						<div style={modalType === "new" ? { display: "none" } : { display: "block" }}>
							<AttachExistingComponents handleCreateModal={props.handleCreateModal} ref={attachRef} />
						</div>
					</GridItem>
				</Grid>
			</Modal>
			{modalData.pageChange && (
				<Modal
					title={`Create and Add ${modalData.componentName}`}
					variant={ModalVariant.medium}
					id="add-component-form-modal"
					className="add-component-form-modal"
					appendTo={document.getElementById("modal-div-container") ?? document.body}
					isOpen
					onClose={() => {
						props.handleCreateModal(false);
						document.body.classList.remove("stop-scrolling");
					}}
					actions={[
						<Button
							key="create-form"
							variant="primary"
							data-analytics-category="Product Info|Add Component"
							isLoading={modalButtonStatus}
							isDisabled={modalButtonStatus}
							onClick={() => {
								step2Ref?.current?.handleSubmit();
							}}
						>
							Add Component
						</Button>,
						<Button
							key="back"
							variant="secondary"
							isDisabled={modalButtonStatus}
							onClick={() => {
								setModalData(initialModalValue);
							}}
						>
							Back
						</Button>,
						<Button
							key="cancel-form"
							variant="link"
							isDisabled={modalButtonStatus}
							onClick={() => {
								props.handleCreateModal(false);
								document.body.classList.remove("stop-scrolling");
							}}
						>
							Cancel
						</Button>,
					]}
				>
					<OpenStackInfraCreateForm handleCreateModal={props.handleCreateModal} ref={step2Ref} />
				</Modal>
			)}
		</>
	);
};
